/**
 * TypeScript types for the buildings system.
 */

import type { ResourceData } from '@/components/HexResources';
import type { Character } from '@/types/characters';
import type { ItemRecipe } from '@/types/items';
import type { CraftingCost } from '@/types/crafting';

/**
 * Represents a building type definition (static data).
 */
export interface BuildingType {
    id: number;
    name: string;
    slug: string;
    rank: number;
    description: string | null;
    imagePath: string | null;
}

/**
 * Represents a building type available for construction.
 * Includes cost and affordability information.
 */
export interface AvailableBuildingType extends BuildingType {
    baseCost: ResourceData;
    buildTimeMinutes: number;
    requirements: string[];
    canAfford: boolean;
}

/**
 * Represents a character assigned to a building slot.
 */
export interface AssignedCharacter {
    id: number;
    name: string;
    class: {
        value: string;
        label: string;
    };
    slot: number;
}

/**
 * Represents a building instance on a hex.
 */
export interface HexBuilding {
    id: number;
    hexId: number;
    /** Optional decorative/roleplay name for the building */
    customName: string | null;
    buildingType: BuildingType;
    level: number;
    stars: number;
    isUpgradingLevel: boolean;
    isUpgradingStars: boolean;
    upgradeStartedAt: string | null;
    upgradeCompletesAt: string | null;
    upgradeCost: ResourceData | null;
    isConstructionComplete: boolean;
    isUpgrading: boolean;
    timeRemainingMinutes: number | null;
    upgradeProgressPercent: number | null;
    canUpgradeLevel: boolean;
    canUpgradeStars: boolean;
    /** Cost to upgrade to the next level (null if at max level) */
    nextLevelCost: ResourceData | null;
    /** Cost to upgrade to the next star level (null if at max stars) */
    nextStarCost: ResourceData | null;
    /** Characters assigned to this building */
    assignedCharacters?: AssignedCharacter[];
    /** Number of character slots available (equals stars) */
    characterSlotCount?: number;
    /** Number of characters currently assigned */
    assignedCharacterCount?: number;
}

/**
 * Response from the buildings index endpoint.
 */
export interface BuildingsIndexResponse {
    buildings: HexBuilding[];
    canBuild: boolean;
}

/**
 * Response from the available buildings endpoint.
 */
export interface AvailableBuildingsResponse {
    availableTypes: AvailableBuildingType[];
    userResources: ResourceData;
}

/**
 * Response from the building show endpoint.
 */
export interface BuildingShowResponse {
    building: HexBuilding;
    nextLevelCost: ResourceData | null;
    nextStarCost: ResourceData | null;
    nextLevelTime: number | null;
    nextStarTime: number | null;
}

/**
 * Level cost multipliers (from BUILDINGS.md).
 */
export const LEVEL_MULTIPLIERS: Record<number, number> = {
    1: 1,
    2: 10,
    3: 60,
    4: 120,
    5: 240,
};

/**
 * Build time in minutes per rank.
 */
export const RANK_BUILD_TIMES: Record<number, number> = {
    1: 1,
    2: 5,
    3: 25,
    4: 125,
    5: 625,
};

/**
 * Star upgrade time in minutes.
 */
export const STAR_UPGRADE_TIMES: Record<number, number> = {
    2: 10,
    3: 100,
    4: 1000,
    5: 10000,
};

/**
 * Format minutes into a human-readable duration string.
 */
export function formatDuration(minutes: number): string {
    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours < 24) {
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    if (remainingHours > 0) {
        return `${days}d ${remainingHours}h`;
    }

    return `${days}d`;
}

/**
 * Get the rank color class for styling.
 */
export function getRankColorClass(rank: number): string {
    switch (rank) {
        case 1:
            return 'text-gray-400';
        case 2:
            return 'text-green-400';
        case 3:
            return 'text-blue-400';
        case 4:
            return 'text-purple-400';
        case 5:
            return 'text-yellow-400';
        default:
            return 'text-gray-400';
    }
}

/**
 * Recipe available for crafting at a specific building.
 * Extends ItemRecipe with crafting cost and requirement info.
 */
export interface BuildingRecipe extends ItemRecipe {
    /** Time to craft in minutes */
    crafting_time_minutes: number;
    /** Human-readable crafting time */
    crafting_time_display: string;
    /** Resource costs to craft */
    crafting_cost: CraftingCost;
    /** Building level required to craft this recipe */
    required_level: number;
    /** Building stars required to craft this recipe */
    required_stars: number;
    /** Whether current building level meets requirements */
    meets_level_requirement: boolean;
    /** Whether current building stars meets requirements */
    meets_stars_requirement: boolean;
    /** Whether recipe is currently available (meets all requirements) */
    is_available: boolean;
}

